// src/services/google.service.js
// Modern Native Integration using "Places API (New)" - 2026 Standard
// Focused on Production Optimization and Cost Minimization

// --- CACHE LAYER ---
const GOOGLE_CACHE = new Map();
const CACHE_TTL = 10 * 60 * 1000; // 10 minutes

const getCachedResults = (query) => {
  const entry = GOOGLE_CACHE.get(query);
  if (entry && (Date.now() - entry.timestamp < CACHE_TTL)) {
    console.log(`📦 [CacheLayer] Hit for: "${query}"`);
    return entry.results;
  }
  return null;
};

const setCachedResults = (query, results) => {
  GOOGLE_CACHE.set(query, { results, timestamp: Date.now() });
};

// --- SESSION MANAGER ---
const SESSIONS = {}; // tracks sessionToken per instanceId (e.g. 'departure', 'arrival')

const getSessionToken = async (instanceId) => {
  if (SESSIONS[instanceId]) return SESSIONS[instanceId];
  
  await initModernPlaces();
  const token = new cachedSessionTokenClass();
  SESSIONS[instanceId] = token;
  console.log(`🎟️ [SessionManager] New session started for: ${instanceId}`);
  return token;
};

export const purgeSession = (instanceId) => {
  if (SESSIONS[instanceId]) {
    delete SESSIONS[instanceId];
    console.log(`🗑️ [SessionManager] Session purged for: ${instanceId}`);
  }
};

// --- CORE SERVICE ---
let cachedAutocompleteSuggestion = null;
let cachedPlace = null;
let cachedSessionTokenClass = null;

/**
 * Initializes the modern Places classes.
 */
const initModernPlaces = async () => {
  if (cachedAutocompleteSuggestion) return;
  
  if (!window.google || !window.google.maps || !window.google.maps.importLibrary) {
    throw new Error('Google Maps Core not ready');
  }

  const { AutocompleteSuggestion, AutocompleteSessionToken, Place } = await window.google.maps.importLibrary("places");
  cachedAutocompleteSuggestion = AutocompleteSuggestion;
  cachedSessionTokenClass = AutocompleteSessionToken;
  cachedPlace = Place;
  console.log('🏗️ [GoogleService] modern libraries initialized');
};

/**
 * Fetch place predictions with session management and caching.
 */
export const googleAutocomplete = async (query, instanceId = 'default') => {
  if (!query || query.length < 3) return [];
  
  // 1. Check Cache
  const cached = getCachedResults(query);
  if (cached) return cached;

  console.log(`🔍 [GoogleService] Autocomplete for: "${query}" [Instance: ${instanceId}]`);

  try {
    await initModernPlaces();
    const sessionToken = await getSessionToken(instanceId);
    
    const request = { 
      input: query,
      sessionToken: sessionToken,
    };

    const { suggestions } = await cachedAutocompleteSuggestion.fetchAutocompleteSuggestions(request);
    
    // Map to simple structure
    const results = (suggestions || []).map(s => ({
      place_id: s.placePrediction.placeId,
      description: s.placePrediction.text.text,
      main_text: s.placePrediction.text.text,
      secondary_text: ''
    })).slice(0, 5); // Limit to 5 results for cost/UX

    // 2. Update Cache
    setCachedResults(query, results);
    
    return results;
  } catch (err) {
    console.error('🔥 [GoogleService] Autocomplete error:', err);
    throw err;
  }
};

/**
 * Fetch place details and close the billing session.
 */
export const googlePlaceDetails = async (placeId, instanceId = 'default') => {
  console.log(`🔍 [GoogleService] Place Details for: ${placeId}`);

  try {
    await initModernPlaces();
    const sessionToken = await getSessionToken(instanceId);
    
    const place = new cachedPlace({
      id: placeId,
      requestedLanguage: 'fr'
    });

    console.log(`🚀 [GoogleService] Fetching modern fields for: ${placeId}`);
    
    const response = await place.fetchFields({
      fields: ['location', 'formattedAddress', 'displayName'],
      sessionToken: sessionToken
    });

    // 3. CRITICAL: End session after successful details retrieval
    purgeSession(instanceId);

    // Defensive extraction: handle variations in key casing and object structures
    const p = response.place || response.Place || place;
    console.log('📡 [GoogleService] Raw Place Data:', p);

    const location = p.location;
    if (!location) throw new Error('No location found in Place details');

    // Handle both legacy lat() and modern latitude/longitude
    const lat = typeof location.lat === 'function' ? location.lat() : (location.latitude ?? location.lat);
    const lng = typeof location.lng === 'function' ? location.lng() : (location.longitude ?? location.lng);

    // Handle displayName object vs string
    const name = typeof p.displayName === 'object' ? (p.displayName?.text || p.displayName) : p.displayName;

    return {
      geometry: {
        location: { lat, lng }
      },
      formatted_address: p.formattedAddress || name || 'Unknown Address',
      name: name || 'Location'
    };
  } catch (err) {
    console.error('🔥 [GoogleService] Details error:', err);
    throw err;
  }
};
